import i18n from '../i18n'

export function formatPhone(raw: string): string {
  // US-only formatter — per CLAUDE.md the app dials only US numbers.
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

export function formatDuration(seconds: number | null | undefined): string {
  // Numeric MM:SS — locale-independent.
  if (seconds == null) return '—'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function formatTimestamp(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString(i18n.language || undefined)
}

export function formatDateTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString(i18n.language || undefined)
}

const rtfCache = new Map<string, Intl.RelativeTimeFormat>()

function getRtf(lang: string): Intl.RelativeTimeFormat {
  let rtf = rtfCache.get(lang)
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
    rtfCache.set(lang, rtf)
  }
  return rtf
}

export function timeAgo(ts: number): string {
  const lang = i18n.language || 'en'
  const diff = Date.now() / 1000 - ts
  const rtf = getRtf(lang)
  if (diff < 60) return rtf.format(-Math.round(diff), 'second')
  if (diff < 3600) return rtf.format(-Math.floor(diff / 60), 'minute')
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), 'hour')
  return rtf.format(-Math.floor(diff / 86400), 'day')
}

export function elapsedSince(startTs: number): string {
  const elapsed = Date.now() / 1000 - startTs
  return formatDuration(elapsed)
}

export function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : digits
}

/** Progressive US-phone mask for controlled inputs: "+1 (XXX) XXX-XXXX".
 *
 * A leading "1" is treated as the US country-code prefix and is consumed
 * into the "+1 (" head. Without this, typing the first "1" would strip
 * to an empty local part and visually wipe the field — a UX trap if the
 * user is typing the country code by hand. The country-code branch
 * therefore returns "+1 (" while waiting for the area code instead of "".
 */
export function maskUSPhone(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 11)
  const hasCountryCode = digits.startsWith('1')
  const local = (hasCountryCode ? digits.slice(1) : digits).slice(0, 10)
  if (local.length === 0) return hasCountryCode ? '+1 (' : ''
  if (local.length <= 3) return `+1 (${local}`
  if (local.length <= 6) return `+1 (${local.slice(0, 3)}) ${local.slice(3)}`
  return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/** Convert a US phone number to E.164 format (e.g. "+17869093661"). */
export function toE164(phone: string, countryCode = '+1'): string {
  if (phone.startsWith('+') && /^\+\d{10,15}$/.test(phone)) return phone
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `${countryCode}${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return phone
}

export function phonesMatch(a: string, b: string): boolean {
  return normalizePhone(a) === normalizePhone(b)
}

export function cn(...classes: (string | undefined | false | null)[]): string {
  return classes.filter(Boolean).join(' ')
}
